/* Which key does each synced table have? (migration #33)
   The client-synced tables used a GLOBAL primary key on `id`. The app mints
   ids in the browser, so two workspaces that minted the same one collided:
   the second workspace's save became a conflict-update on the first
   workspace's row, which RLS refuses, and that record never reached the
   cloud. Migration #33 makes the key (workspace_id, id).

   An upsert has to name the key it conflicts on, and naming the wrong one is
   an error (Postgres 42P10), so the save asks the database once per session —
   fw_sync_keys(), which #33 installs; absent before it, meaning the old key —
   and if an upsert still names the wrong key (the owner ran #33 while this tab
   was open) it flips that table and sends the rows again. The app keeps saving
   on either side of the migration.

   Pure; unit-tested in scripts/unit-keys.mjs. */

export type KeyMode = "composite" | "legacy";
export type KeyModes = Record<string, KeyMode>;

/* the tables the whole-state save and the AI sync upsert (lib/supabase.ts) */
export const SYNC_TABLES = ["customers", "crews", "products", "projects", "quotes", "work_orders", "payments", "sequences"];
export const COMPOSITE_TARGET = "workspace_id,id";
export const LEGACY_TARGET = "id";

/* fw_sync_keys()'s answer ({ quotes: "workspace_id,id", … }) → a mode per
   table. Anything else — no answer, an error, a table it didn't list — is
   the old key, which is what every database had before #33. */
export function keyModesFrom(data: any): KeyModes {
  const out: KeyModes = {};
  const ok = !!data && typeof data === "object" && !Array.isArray(data);
  for (const t of SYNC_TABLES) {
    const cols = ok ? String(data[t] == null ? "" : data[t]).replace(/\s+/g, "") : "";
    out[t] = cols === COMPOSITE_TARGET ? "composite" : "legacy";
  }
  return out;
}

/* the onConflict for an upsert into `table` */
export function conflictTarget(modes: KeyModes | null | undefined, table: string): string {
  return modes && modes[table] === "composite" ? COMPOSITE_TARGET : LEGACY_TARGET;
}

/* "there is no unique or exclusion constraint matching the ON CONFLICT
   specification" — the upsert named a key the table doesn't have */
export function isConflictSpecError(err: any): boolean {
  if (!err) return false;
  if (String(err.code || "") === "42P10") return true;
  return /no unique or exclusion constraint matching the ON CONFLICT/i.test(String(err.message || ""));
}

export function flipMode(m: KeyMode | undefined): KeyMode {
  return m === "composite" ? "legacy" : "composite";
}
